import { Link } from "react-router-dom";
import { ArrowRight, Compass, ShieldAlert, Sparkles, LayoutDashboard, FileEdit } from "lucide-react";
import BrandHeader from "@/components/BrandHeader";
import { useI18n } from "@/i18n";

const FeatureItem = ({ icon: Icon, title, desc, testid }) => (
  <div
    data-testid={testid}
    className="card-premium corner-frame rounded-2xl p-6 lg:p-7"
  >
    <div className="flex items-center gap-3 mb-3">
      <div className="w-9 h-9 rounded-lg bg-gold-500/10 border border-gold-500/30 flex items-center justify-center">
        <Icon className="w-4 h-4 text-gold-300" strokeWidth={1.6} />
      </div>
      <h3 className="font-serif-cn text-gold-100 text-lg">{title}</h3>
    </div>
    <p className="text-sm text-slate-300 leading-relaxed">{desc}</p>
  </div>
);

export default function Landing() {
  const { t, locale } = useI18n();

  const sections = [
    t("landing.section1"),
    t("landing.section2"),
    t("landing.section3"),
    t("landing.section4"),
    t("landing.section5"),
    t("landing.section6"),
    t("landing.section7"),
    t("landing.section8"),
    t("landing.section9"),
  ];

  const features = [
    {
      title: t("landing.feature1Title"),
      desc: t("landing.feature1Desc"),
    },
    {
      title: t("landing.feature2Title"),
      desc: t("landing.feature2Desc"),
    },
    {
      title: t("landing.feature3Title"),
      desc: t("landing.feature3Desc"),
    },
  ];

  const reportBenefits = [
    t("landing.reportBenefit1"),
    t("landing.reportBenefit2"),
    t("landing.reportBenefit3"),
    t("landing.reportBenefit4"),
    t("landing.reportBenefit5"),
    t("landing.reportBenefit6"),
    t("landing.reportBenefit7"),
  ];

  const aiBenefits = [
    t("landing.aiBenefit1"),
    t("landing.aiBenefit2"),
    t("landing.aiBenefit3"),
  ];

  return (
    <div className="min-h-screen bg-navy-900 relative overflow-hidden" data-testid="landing-page">
      {/* Ambient watermark layers */}
      <div className="pointer-events-none absolute inset-0 opacity-70 bg-watermark-compass" />
      <div className="pointer-events-none absolute inset-0 opacity-[0.25] bg-grid" />
      <div className="pointer-events-none absolute -top-32 -left-24 w-[520px] h-[520px] rounded-full bg-gold-500/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -right-24 w-[560px] h-[560px] rounded-full bg-[#1E3A8A]/20 blur-3xl" />

      <div className="relative z-10">
        <BrandHeader />

        {/* Hero */}
        <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-24">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-7 fade-up">
              <div
                data-testid="hero-eyebrow"
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-gold-500/40 bg-gold-500/5 mb-6"
              >
                <Sparkles className="w-3.5 h-3.5 text-gold-300" />
                <span className="text-[11px] tracking-[0.32em] uppercase font-mono text-gold-200">
                  {t("landing.eyebrow")}
                </span>
              </div>

              <h1
                data-testid="hero-title-cn"
                className="font-serif-cn text-4xl sm:text-5xl lg:text-6xl leading-[1.15] text-gold-100"
              >
                {t("landing.heroTitlePrefix")}<span className="text-gold-gradient">{t("landing.heroTitleHighlight1")}</span>{locale === "zh" ? "，" : ""}
                <br className="hidden sm:block" />
                {t("landing.heroTitleMiddle")}<span className="text-gold-gradient">{t("landing.heroTitleHighlight2")}</span>
              </h1>

              <div className="mt-4 text-xs tracking-[0.35em] uppercase font-mono text-gold-400/80" data-testid="hero-title-en">
                {t("landing.heroSubtitle")}
              </div>

              <p
                data-testid="hero-subtext"
                className="mt-8 text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl"
              >
                {t("landing.heroDescriptionLine1")}
                <br className="hidden sm:block" />
                {t("landing.heroDescriptionLine2")}
              </p>

              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link to="/checkout" data-testid="landing-start-button">
                  <button className="btn-gold rounded-full px-8 py-3.5 inline-flex items-center gap-2 text-sm">
                    {t("landing.start")}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </Link>
                <Link to="/dashboard" data-testid="landing-view-report-button">
                  <button className="btn-ghost-gold rounded-full px-6 py-3 text-sm">
                    {t("landing.viewReport")}
                  </button>
                </Link>
              </div>

              <div className="hairline-gold mt-14" />

              <div
                data-testid="landing-disclaimer"
                className="mt-6 flex items-start gap-3 max-w-2xl"
              >
                <ShieldAlert className="w-4 h-4 text-gold-400/90 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-slate-400 leading-relaxed">
                  {t("landing.disclaimer")}
                </p>
              </div>
            </div>

            {/* Right visual card */}
            <div className="lg:col-span-5 fade-up delay-2">
              <div className="relative">
                <div className="absolute -inset-6 rounded-3xl bg-gold-500/5 blur-2xl" />
                <div className="relative card-premium corner-frame rounded-3xl p-8">
                  <div className="flex items-center justify-between mb-6">
                    <div className="text-[10px] tracking-[0.35em] uppercase font-mono text-gold-400/80">
                      {t("landing.reportSectionsLabel")}
                    </div>
                    <span className="text-[10px] tracking-widest px-2 py-0.5 rounded-full border border-gold-500/40 text-gold-200">
                      V2.9.9
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-3" data-testid="landing-sections">
                    {sections.map((label) => (
                      <div
                        key={label}
                        className="rounded-xl border border-gold-500/20 bg-navy-800/60 px-4 py-3 flex items-center gap-2"
                      >
                        <Compass className="w-3.5 h-3.5 text-gold-300 flex-shrink-0" />
                        <span className="text-sm text-gold-100">{label}</span>
                      </div>
                    ))}
                  </div>

                  <div className="hairline-gold my-6" />

                  <p className="text-xs text-slate-400 leading-relaxed">
                    {t("landing.reportSectionsDescription")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Commercial product presentation */}
        <section
          className="max-w-7xl mx-auto px-6 lg:px-10 pb-24"
          data-testid="landing-commercial-products"
        >
          <div className="text-center max-w-3xl mx-auto mb-10">
            <div className="text-[10px] tracking-[0.35em] uppercase font-mono text-gold-400/80">
              {t("landing.commercialEyebrow")}
            </div>
            <h2 className="mt-3 font-serif-cn text-3xl sm:text-4xl text-gold-100">
              {t("landing.commercialTitle")}
            </h2>
            <p className="mt-4 text-sm sm:text-base text-slate-300 leading-relaxed">
              {t("landing.commercialDescription")}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <div
              className="card-premium corner-frame rounded-3xl p-7 sm:p-8"
              data-testid="commercial-product-report"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase font-mono text-gold-400/80">
                    {t("landing.reportProductLabel")}
                  </div>
                  <h3 className="mt-2 font-serif-cn text-2xl text-gold-100">
                    {t("landing.reportProductTitle")}
                  </h3>
                </div>
                <span
                  className="text-[10px] tracking-widest uppercase px-3 py-1 rounded-full border border-emerald-400/40 text-emerald-200 bg-emerald-500/10"
                  data-testid="commercial-report-status"
                >
                  {t("landing.reportProductStatus")}
                </span>
              </div>

              <p className="mt-5 text-sm text-slate-300 leading-relaxed">
                {t("landing.reportProductDescription")}
              </p>

              <div className="mt-6 space-y-3">
                {reportBenefits.map((benefit) => (
                  <div key={benefit} className="flex items-start gap-3">
                    <span className="text-gold-300 mt-0.5">•</span>
                    <span className="text-sm text-slate-200 leading-relaxed">{benefit}</span>
                  </div>
                ))}
              </div>

              <div className="mt-8">
                <Link to="/checkout" data-testid="commercial-report-cta">
                  <button className="btn-gold rounded-full px-7 py-3 inline-flex items-center gap-2 text-sm">
                    {t("landing.reportProductCta")}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </Link>
              </div>
            </div>

            <div
              className="card-premium corner-frame rounded-3xl p-7 sm:p-8"
              data-testid="commercial-product-ai"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase font-mono text-gold-400/80">
                    {t("landing.aiProductLabel")}
                  </div>
                  <h3 className="mt-2 font-serif-cn text-2xl text-gold-100">
                    {t("landing.aiProductTitle")}
                  </h3>
                </div>
                <span
                  className="text-[10px] tracking-widest uppercase px-3 py-1 rounded-full border border-gold-500/30 text-gold-300 bg-gold-500/5"
                  data-testid="commercial-ai-status"
                >
                  {t("landing.aiProductStatus")}
                </span>
              </div>

              <p className="mt-5 text-sm text-slate-300 leading-relaxed">
                {t("landing.aiProductDescription")}
              </p>

              <div className="mt-6 space-y-3">
                {aiBenefits.map((benefit) => (
                  <div key={benefit} className="flex items-start gap-3">
                    <span className="text-gold-300 mt-0.5">•</span>
                    <span className="text-sm text-slate-200 leading-relaxed">{benefit}</span>
                  </div>
                ))}
              </div>

              <div className="hairline-gold my-6" />

              <div
                className="flex items-start gap-3"
                data-testid="commercial-ai-boundary"
              >
                <ShieldAlert className="w-4 h-4 text-gold-400/90 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-slate-400 leading-relaxed">
                  {t("landing.aiProductBoundary")}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Feature strip */}
        <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24" data-testid="landing-features">
          <div className="grid md:grid-cols-3 gap-6">
            <FeatureItem
              testid="feature-analysis"
              icon={LayoutDashboard}
              title={features[0].title}
              desc={features[0].desc}
            />
            <FeatureItem
              testid="feature-strategy"
              icon={Sparkles}
              title={features[1].title}
              desc={features[1].desc}
            />
            <FeatureItem
              testid="feature-wealth"
              icon={FileEdit}
              title={features[2].title}
              desc={features[2].desc}
            />
          </div>
        </section>

        <footer className="border-t border-gold-500/15 py-8">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="text-xs text-slate-400">
              {t("landing.footerBrand")}
            </div>
            <div className="text-[10px] tracking-[0.35em] uppercase font-mono text-gold-400/80">
              {t("landing.footerNote")}
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
